import i18n from '../../i18n/config';
// Sidebar.jsx - WEBSOCKET VERSION: Usa useAlarmSubscription
import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Collapse,
  useTheme,
  Chip,
  Avatar,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemAvatar,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';

import {
  Store as StoreIcon,
  AdminPanelSettings as AdminStoreIcon,
  Inventory2 as ProductsIcon,
  ShoppingCart as CartIcon,
  ReceiptLong as OrdersIcon,
  NotificationsActive as MonitoringIcon,
  Assessment as ReportsIcon,
  PlaylistAddCheck as ActionSummaryIcon,
  ConfirmationNumber as TicketIcon,
  Apartment as CompanyIcon,
  Groups as UsersIcon,
  FactCheck as AuditIcon,
  Settings as SettingsIcon,
  DirectionsCar as VehiclesIcon,
  Sensors as SensorsIcon,
  Router as ReceiversIcon,
  Fence as GeofencesIcon,
  Badge as OperatorsIcon,
  Dashboard as DashboardIcon,
  Person as ProfileIcon,
  HelpOutline as HelpIcon,
  ExpandLess,
  ExpandMore,
  KeyboardArrowDown as ArrowDownIcon,
  CheckCircle as CheckIcon,
  Receipt as ReceiptLong,
  Settings,
  Business as BusinessIcon,
  AutoAwesome as AssistantIcon,
  Forum as ForumIcon,
  Insights as InsightsIcon,
  Campaign as CampaignIcon,
  School as TrainingIcon
} from '@mui/icons-material';

import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { getCachedUnitPreferences, setCachedUnitPreferences, subscribeUnitPreferences } from '../../services/unitPreferencesStore';

import { useAlarmSubscription } from '../../hooks/useWebSocket';
import { useNotification } from '../common/GlobalNotifications';

// La versión sale del build, no de un literal en el JSX: antes el pie decía
// "Versión 1.2.0" escrito a mano, así que no cambiaba nunca al desplegar.
const APP_VERSION = process.env.REACT_APP_VERSION || '';

const SCHEMATICS_COMPANY_SCOPE_KEY = 'tpms_schematics_company_scope';
const SCHEMATICS_SCOPE_EVENT = 'tpms-schematics-scope-changed';

const Sidebar = ({
  open,
  collapsed,
  onClose,
  widthExpanded,
  widthCollapsed,
  isMobile,
  navbarHeight
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();

  const {
    user,
    currentCompany,
    companies = [],
    hasPermission = () => false,
    switchCompany,
    hasMultipleCompanies = false,
    currentCompanyName = '',
    loading = false
  } = useAuth() || {};

  // 🔄 WEBSOCKET: Usar useAlarmSubscription en lugar de useAlarmMonitoring
  // Cambio: alarmCount → activeAlarmCount
  const { activeAlarmCount: alarmCount = 0 } = useAlarmSubscription() || {};

  const [expandedSections, setExpandedSections] = useState({});
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState(null);
  const [switchingCompany, setSwitchingCompany] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [allCompaniesView, setAllCompaniesView] = useState(
    () => localStorage.getItem(SCHEMATICS_COMPANY_SCOPE_KEY) === 'all'
  );
  const [unitPreferences, setUnitPreferences] = useState(getCachedUnitPreferences());

  useEffect(() => {
    setAllCompaniesView(localStorage.getItem(SCHEMATICS_COMPANY_SCOPE_KEY) === 'all');
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = subscribeUnitPreferences((preferences) => {
      if (isMounted) {
        setUnitPreferences(preferences || null);
      }
    });

    const loadUnitPreferences = async () => {
      try {
        const response = await api.unitPreferences.getMyPreferences();
        if (isMounted) {
          const nextPreferences = response.data || null;
          setUnitPreferences(nextPreferences);
          setCachedUnitPreferences(nextPreferences);
        }
      } catch (error) {
        if (isMounted) {
          setUnitPreferences(null);
        }
      }
    };

    loadUnitPreferences();

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [currentCompany?.company_id]);

  const unitsLabel = useMemo(() => {
    if (!unitPreferences) {
      return 'PSI • C • G';
    }

    const pressureUnit = unitPreferences.pressure_unit || 'PSI';
    const temperatureUnit = unitPreferences.temperature_unit || 'C';
    const accelerationUnit = unitPreferences.acceleration_unit || 'G';

    return `${pressureUnit} • ${temperatureUnit} • ${accelerationUnit}`;
  }, [unitPreferences]);

  // 🔧 Verificar si es admin de Arsenal
  const isArsenalAdmin = () => {
    if (!user || !user.email) return false;
    const isArsenalEmail = user.email.toLowerCase().endsWith(t('hardcoded.components_common_sidebar.arsenalmx'));
    const userRole = currentCompany?.role || user?.role;
    return isArsenalEmail && (userRole === 'admin' || userRole === 'superadmin');
  };

  const checkPermission = (requiredPermission) => {
    if (!hasPermission || typeof hasPermission !== 'function') {
      return false;
    }

    const userRole = currentCompany?.role || user?.role;

    const roleHierarchy = {
      'user': 1,
      'manager': 2,
      'admin': 3,
      'superadmin': 4
    };

    const permissionHierarchy = {
      'user': 1,
      'manager': 2,
      'admin': 3,
      'superadmin': 4
    };

    const userLevel = roleHierarchy[userRole] || 0;
    const requiredLevel = permissionHierarchy[requiredPermission] || 999;

    if (userRole === 'superadmin' || userRole === 'admin') {
      return true;
    }

    if (userRole === 'manager' && requiredLevel <= 2) {
      return true;
    }

    if (userRole === 'user' && requiredLevel <= 1) {
      return true;
    }

    return hasPermission(requiredPermission);
  };

  if (loading) {
    return (
      <Box sx={{
        width: isMobile ? widthExpanded : (collapsed ? widthCollapsed : widthExpanded),
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRight: '1px solid',
        borderRightColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{
        width: isMobile ? widthExpanded : (collapsed ? widthCollapsed : widthExpanded),
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRight: '1px solid',
        borderRightColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Alert severity="error">{t('hardcoded.components_common_sidebar.error_cargando_datos_de_usuario')}</Alert>
      </Box>
    );
  }

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const handleExpandClick = (section) => {
    if (!collapsed) {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    }
  };

  // Paths "hoja" que NO deben activarse por prefijo cuando hay sub-rutas
  // hermanas más específicas. Por ejemplo, estando en '/reports/acciones' el
  // item '/reports' (Reportes TPMS) NO debe sombrearse — usaríamos match exacto.
  const EXACT_MATCH_PATHS = new Set([
    '/reports',
    '/tasks',
    '/billing',
    '/store',
    '/vehicles',
  ]);

  const isActive = (path) => {
    if (!path) return false;

    // Caso especial histórico: /vehicles/schematics (Panel de Control) no debe activar /vehicles.
    if (path === '/vehicles') {
      return location.pathname === '/vehicles';
    }

    // Para items "hoja" con sub-rutas hermanas, exigir coincidencia exacta.
    if (EXACT_MATCH_PATHS.has(path)) {
      return location.pathname === path;
    }

    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleCompanyMenuOpen = (event) => {
    setCompanyMenuAnchor(event.currentTarget);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchor(null);
    setCompanySearchTerm('');
  };

  const handleSwitchCompany = async (companyId) => {
    localStorage.removeItem(SCHEMATICS_COMPANY_SCOPE_KEY);
    setAllCompaniesView(false);
    window.dispatchEvent(new CustomEvent(SCHEMATICS_SCOPE_EVENT, { detail: { scope: 'current' } }));

    if (companyId === currentCompany?.company_id) {
      handleCompanyMenuClose();
      return;
    }

    setSwitchingCompany(true);
    try {
      const result = await switchCompany(companyId);

      if (result.success) {
        showSuccess(t('hardcoded.components_common_sidebar.cambiado_a_company_name', { company_name: companies.find(c => c.company_id === companyId)?.company_name }));
        window.location.reload();
      } else {
        showError(result.message || t('hardcoded.components_common_sidebar.error_al_cambiar_de_empresa'));
      }
    } catch (error) {
      showError(t('hardcoded.components_common_sidebar.error_al_cambiar_de_empresa'));
    } finally {
      setSwitchingCompany(false);
      handleCompanyMenuClose();
    }
  };

  const handleSwitchAllCompanies = () => {
    localStorage.setItem(SCHEMATICS_COMPANY_SCOPE_KEY, 'all');
    setAllCompaniesView(true);
    window.dispatchEvent(new CustomEvent(SCHEMATICS_SCOPE_EVENT, { detail: { scope: 'all' } }));
    handleCompanyMenuClose();
    navigate('/vehicles/schematics');
  };

  const navigationItems = [
    {
      id: 'dashboard',
      label: t('hardcoded.components_common_sidebar.panel_de_control'),
      icon: <DashboardIcon />,
      path: '/vehicles/schematics',
      permission: 'user'
    },
    {
      id: 'monitoring',
      label: t('hardcoded.components_common_sidebar.monitoreo'),
      icon: <MonitoringIcon />,
      permission: 'user',
      // 🔄 WEBSOCKET: Mostrar badge con alarmas activas
      badge: alarmCount > 0 ? alarmCount : null,
      badgeColor: 'error',
      children: [
        {
          id: 'alarm-profiles',
          label: t('hardcoded.components_common_sidebar.perfiles'),
          icon: <AuditIcon />,
          path: '/alarm-profiles',
          permission: 'manager'
        },
        {
          id: 'alarm-tickets',
          label: t('hardcoded.components_common_sidebar.tickets'),
          icon: <TicketIcon />,
          path: '/alarm-tickets',
          permission: 'user',
          // 🔄 WEBSOCKET: Badge en tickets también
          badge: alarmCount > 0 ? alarmCount : null,
          badgeColor: 'error'
        },
        {
          // Apartado "Reportes" — agrupa los dos reportes para evitar que
          // estando en /reports/acciones se sombree también /reports.
          id: 'reports',
          label: t('hardcoded.components_common_sidebar.reportes'),
          icon: <ReportsIcon />,
          permission: 'user',
          children: [
            {
              id: 'reports-tpms',
              label: t('hardcoded.components_common_sidebar.reportes_tpms'),
              icon: <ReportsIcon />,
              path: '/reports',
              permission: 'user'
            },
            {
              id: 'reports-action-summary',
              label: t('hardcoded.components_common_sidebar.resumen_de_acciones'),
              icon: <ActionSummaryIcon />,
              path: '/reports/acciones',
              permission: 'user'
            },
          ],
        },
        {
          id: 'tasks',
          label: t('hardcoded.components_common_sidebar.tareas_aps'),
          icon: <AuditIcon />,
          path: '/tasks',
          permission: 'user'
        },
        {
          id: 'chat',
          label: t('hardcoded.components_common_sidebar.chat'),
          icon: <TicketIcon />,
          path: '/chat',
          permission: 'user'
        },
        {
          id: 'ai-assistant',
          label: t('hardcoded.components_common_sidebar.asistente_ia'),
          icon: <AssistantIcon />,
          path: '/chat/ai',
          permission: 'user'
        }
      ]
    },
    {
      id: 'fleet',
      label: t('hardcoded.components_common_sidebar.flota'),
      icon: <VehiclesIcon />,
      permission: 'user',
      children: [
        {
          id: 'vehicles',
          label: t('vehicles.title'),
          icon: <VehiclesIcon />,
          path: '/vehicles',
          permission: 'user'
        },
        {
          id: 'receivers',
          label: t('receivers.title'),
          icon: <ReceiversIcon />,
          path: '/receivers',
          permission: 'user'
        },
        {
          id: 'gps',
          label: t('gps.title'),
          icon: <ReceiversIcon />,
          path: '/gps',
          permission: 'user'
        },
        {
          id: 'geofences',
          label: t('hardcoded.components_fleet.geocercas'),
          icon: <GeofencesIcon />,
          path: '/geofences',
          permission: 'user'
        },
        {
          id: 'sensors',
          label: t('sensors.title'),
          icon: <SensorsIcon />,
          path: '/sensors',
          permission: 'user'
        },
        {
          id: 'operators',
          label: t('hardcoded.components_common_sidebar.operadores'),
          icon: <OperatorsIcon />,
          path: '/operators',
          permission: 'user'
        }
      ]
    },
    {
      id: 'store',
      label: t('hardcoded.components_common_sidebar.tienda'),
      icon: <StoreIcon />,
      permission: 'user',
      children: [
        {
          id: 'store-browse',
          label: t('cart.items'),
          icon: <ProductsIcon />,
          path: '/store',
          permission: 'user'
        },
        {
          id: 'cart',
          label: t('navbar.cart'),
          icon: <CartIcon />,
          path: '/cart',
          permission: 'user'
        },
        {
          id: 'my-orders',
          label: t('hardcoded.components_common_sidebar.mis_ordenes'),
          icon: <OrdersIcon />,
          path: '/orders',
          permission: 'user'
        }
      ]
    }
  ];

  // Empresa: gestión común (managers+). NO incluye opciones internas Arsenal.
  const adminItems = [
    {
      id: 'administration',
      label: t('navbar.company'),
      icon: <CompanyIcon />,
      permission: 'manager',
      children: [
        {
          id: 'users',
          label: t('users.title'),
          icon: <UsersIcon />,
          path: '/users',
          permission: 'manager'
        },
        {
          id: 'audit',
          label: t('audit.title'),
          icon: <AuditIcon />,
          path: '/audit',
          permission: 'manager'
        },
        {
          id: 'chat-analytics',
          label: t('hardcoded.components_common_sidebar.uso_del_chat'),
          icon: <ForumIcon />,
          path: '/chat-analytics',
          permission: 'manager'
        },
        {
          id: 'settings',
          label: t('settings.title'),
          icon: <SettingsIcon />,
          path: '/settings',
          permission: 'manager'
        }
      ]
    }
  ];

  // Cuenta del usuario — aparece PRIMERO en el sidebar por requerimiento UX.
  const userItems = [
    {
      id: 'account',
      label: t('navbar.account'),
      icon: <ProfileIcon />,
      permission: 'user',
      children: [
        {
          id: 'portal-notifications',
          label: 'Mis notificaciones',
          icon: <MonitoringIcon />,
          path: '/notifications',
          permission: 'user'
        },
        {
          id: 'training',
          label: 'Capacitación',
          icon: <TrainingIcon />,
          path: '/training',
          permission: 'user'
        },
        {
          id: 'profile',
          label: t('profile.title'),
          icon: <ProfileIcon />,
          path: '/profile',
          permission: 'user'
        },
        {
          id: 'help',
          label: t('hardcoded.components_common_sidebar.ayuda_y_soporte'),
          icon: <HelpIcon />,
          path: '/help',
          permission: 'user'
        },
        {
          id: 'billing-portal',
          label: t('hardcoded.components_common_sidebar.cobranza'),
          icon: <ReceiptLong />,
          path: '/billing/portal',
          permission: 'user'
        }
      ]
    }
  ];

  // Solo Arsenal — bloque único agrupado al final, visible solo si:
  //   - usuario es @arsenal.mx (señal adicional) Y
  //   - tiene rol admin/superadmin (autorización real).
  // Los hijos individuales también filtran por permission para que un admin
  // Arsenal sin un permiso puntual sólo vea lo que su rol permita.
  const arsenalOnlyItems = isArsenalAdmin()
    ? [
        {
          id: 'arsenal-only',
          label: t('hardcoded.components_common_sidebar.solo_arsenal'),
          icon: <AdminStoreIcon />,
          permission: 'admin',
          badge: t('hardcoded.components_common_sidebar.arsenal'),
          children: [
            {
              id: 'store-admin',
              label: t('hardcoded.components_common_sidebar.administrar_tienda'),
              icon: <AdminStoreIcon />,
              path: '/admin/store',
              permission: 'admin'
            },
            {
              id: 'tpms-defaults-admin',
              label: t('hardcoded.components_common_sidebar.defaults_tpms'),
              icon: <Settings />,
              path: '/admin/tpms-defaults',
              permission: 'admin'
            },
            {
              id: 'system-usage',
              label: t('hardcoded.components_common_sidebar.uso_del_sistema'),
              icon: <InsightsIcon />,
              path: '/admin/system-usage',
              permission: 'admin'
            },
            {
              id: 'notification-audit',
              label: 'Centro de Notificaciones',
              icon: <MonitoringIcon />,
              path: '/admin/notification-audit',
              permission: 'admin'
            },
            {
              id: 'training-admin',
              label: 'Capacitación',
              icon: <TrainingIcon />,
              path: '/admin/training',
              permission: 'admin'
            },
            {
              id: 'adoption-campaigns',
              label: t('hardcoded.components_common_sidebar.campanas_de_adopcion'),
              icon: <CampaignIcon />,
              path: '/admin/adoption-campaigns',
              permission: 'admin'
            },
            {
              id: 'billing',
              label: t('hardcoded.components_common_sidebar.facturacion'),
              icon: <ReceiptLong />,
              permission: 'admin',
              children: [
                {
                  id: 'billing-plans',
                  label: t('hardcoded.components_common_sidebar.planes'),
                  icon: <Settings />,
                  path: '/billing/plans',
                  permission: 'admin'
                },
                {
                  id: 'billing-invoices',
                  label: t('hardcoded.components_common_sidebar.facturas'),
                  icon: <ReceiptLong />,
                  path: '/billing/invoices',
                  permission: 'admin'
                },
                {
                  id: 'billing-companies',
                  label: t('hardcoded.components_common_sidebar.companias'),
                  icon: <BusinessIcon />,
                  path: '/admin/companies',
                  permission: 'admin'
                }
              ]
            }
          ]
        }
      ]
    : [];

  // Orden final del sidebar:
  //   1) Navegación principal (Panel, Monitoreo, Flota, Tienda)
  //   2) Empresa (managers+)
  //   3) Cuenta del usuario (después de Empresa)
  //   4) Solo Arsenal (último bloque, gated)
  const allItems = [
    ...navigationItems,
    ...(checkPermission('manager') ? adminItems : []),
    ...userItems,
    ...arsenalOnlyItems
  ];

  const filteredItems = allItems.filter(item => checkPermission(item.permission));

  const getAllNavigableItems = (items) => {
    const navigableItems = [];
    items.forEach(item => {
      if (item.path) {
        navigableItems.push(item);
      }
      if (item.children) {
        navigableItems.push(...getAllNavigableItems(item.children));
      }
    });
    return navigableItems;
  };

  const allNavigableItems = getAllNavigableItems(filteredItems);

  const renderCollapsedNavItem = (item) => {
    if (!checkPermission(item.permission)) {
      return null;
    }

    const active = isActive(item.path);

    return (
      <Tooltip key={item.id} title={item.label} placement="right" arrow>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            data-tutorial-id={item.id}
            data-tutorial-path={item.path}
            selected={active}
            onClick={() => handleNavigate(item.path)}
            sx={{
              minHeight: 48,
              justifyContent: 'center',
              px: 1.5,
              borderRadius: 1,
              mx: 1,
              position: 'relative',
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              '&:hover': {
                backgroundColor: active ? theme.palette.primary.dark : theme.palette.primary.main + '10',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                color: active ? 'white' : theme.palette.text.primary,
              }}
            >
              {item.icon}
            </ListItemIcon>
            {/* 🔄 WEBSOCKET: Badge para items colapsados */}
            {item.badge && typeof item.badge === 'number' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                  color: 'white',
                  fontSize: '0.6rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700
                }}
              >
                {item.badge > 9 ? '9+' : item.badge}
              </Box>
            )}
            {item.badge && typeof item.badge === 'string' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

  const renderNavItem = (item, depth = 0) => {
    if (!checkPermission(item.permission)) {
      return null;
    }

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedSections[item.id];
    const active = item.path ? isActive(item.path) : false;

    if (hasChildren) {
      return (
        <React.Fragment key={item.id}>
          <ListItem disablePadding>
            <ListItemButton
              data-tutorial-id={item.id}
              onClick={() => handleExpandClick(item.id)}
              sx={{
                pl: 2 + depth * 2,
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '10',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: theme.palette.text.primary,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{item.label}</span>
                    {/* 🔄 WEBSOCKET: Badge numérico para alarmas */}
                    {item.badge && typeof item.badge === 'number' && (
                      <Chip
                        label={item.badge}
                        size="small"
                        color={item.badgeColor || 'error'}
                        sx={{
                          height: 20,
                          fontSize: '0.625rem',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    )}
                    {item.badge && typeof item.badge === 'string' && (
                      <Chip
                        label={item.badge}
                        size="small"
                        color={item.badgeColor || 'primary'}
                        sx={{ height: 20, fontSize: '0.625rem' }}
                      />
                    )}
                  </Box>
                }
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              />
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(child => renderNavItem(child, depth + 1))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton
          data-tutorial-id={item.id}
          data-tutorial-path={item.path}
          selected={active}
          onClick={() => handleNavigate(item.path)}
          sx={{
            pl: 2 + depth * 2,
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
            },
            '&:hover': {
              backgroundColor: active ? theme.palette.primary.dark : theme.palette.primary.main + '10',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: active ? 'white' : theme.palette.text.primary,
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{item.label}</span>
                {/* 🔄 WEBSOCKET: Badge para items hijos */}
                {item.badge && typeof item.badge === 'number' && (
                  <Chip
                    label={item.badge}
                    size="small"
                    color={item.badgeColor || 'error'}
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                )}
                {item.badge && typeof item.badge === 'string' && (
                  <Chip
                    label={item.badge}
                    size="small"
                    color={item.badgeColor || 'primary'}
                    sx={{ height: 20, fontSize: '0.625rem' }}
                  />
                )}
              </Box>
            }
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 500,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const currentWidth = isMobile ? widthExpanded : (collapsed ? widthCollapsed : widthExpanded);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {(!collapsed || isMobile) && (
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
            minHeight: 64,
          }}
        >
          {hasMultipleCompanies ? (
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCompanyMenuOpen}
              disabled={switchingCompany}
              startIcon={<CompanyIcon />}
              endIcon={<ArrowDownIcon />}
              sx={{
                justifyContent: 'space-between',
                textTransform: 'none',
                py: 1.5,
                px: 2,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ textAlign: 'left', overflow: 'hidden' }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  noWrap
                  sx={{ color: 'text.primary' }}
                >
                  {allCompaniesView ? t('hardcoded.components_common_sidebar.todas_las_empresas') : (currentCompanyName || t('hardcoded.components_auth_companyselector.seleccionar_empresa'))}
                </Typography>
              </Box>
            </Button>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CompanyIcon color="primary" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  noWrap
                  sx={{ color: 'primary.main' }}
                >
                  {currentCompanyName || t('hardcoded.components_auth_register.arsenal_preventive_sensors')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('hardcoded.components_common_sidebar.sistema_de_monitoreo_tpms')}</Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {collapsed && !isMobile && (
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
            minHeight: 64,
            alignItems: 'center'
          }}
        >
          <Tooltip
            title={allCompaniesView ? t('hardcoded.components_common_sidebar.todas_las_empresas') : (currentCompanyName || t('hardcoded.components_auth_register.arsenal_preventive_sensors'))}
            placement="right"
            arrow
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: theme.palette.primary.main,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {allCompaniesView ? 'T' : (currentCompanyName?.charAt(0)?.toUpperCase() || 'A')}
            </Avatar>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <List sx={{ px: collapsed && !isMobile ? 0 : 1 }}>
          {collapsed && !isMobile
            ? allNavigableItems.map(item => renderCollapsedNavItem(item))
            : filteredItems.map(item => renderNavItem(item))
          }
        </List>
      </Box>

      {(!collapsed || isMobile) && user && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderTopColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.secondary.main,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {user?.name} {user?.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email}
              </Typography>
            </Box>
          </Box>

          {/* Rol · unidades · Arsenal en UNA línea. Antes eran tres chips que se
              apilaban y comían la mitad del pie del sidebar en pantallas cortas.
              Las unidades siguen siendo clicables (llevan a sus preferencias) y
              cada parte lleva `title` porque van abreviadas. */}
          <Box
            sx={{
              mb: 2, display: 'flex', alignItems: 'center', gap: 0.75,
              flexWrap: 'nowrap', overflow: 'hidden',
            }}
          >
            <Typography
              variant="caption" fontWeight={700} noWrap
              color="primary.main"
              // `minWidth: 0` es lo que permite que el texto se recorte en vez de
              // empujar fuera el distintivo Arsenal en sidebars estrechos.
              sx={{ minWidth: 0, flexShrink: 1 }}
              title={currentCompany?.role_display || currentCompany?.role || ''}
            >
              {currentCompany?.role_display
                || (currentCompany?.role
                  ? currentCompany.role.charAt(0).toUpperCase() + currentCompany.role.slice(1)
                  : t('audit.table.user'))}
            </Typography>

            <Typography variant="caption" color="text.disabled">·</Typography>

            <Typography
              variant="caption" noWrap
              color="text.secondary"
              onClick={() => handleNavigate('/profile?tab=units')}
              title={`Unidades: ${unitsLabel}`}
              sx={{
                cursor: 'pointer', minWidth: 0, flexShrink: 1,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {unitsLabel}
            </Typography>

            {isArsenalAdmin() && (
              <>
                <Typography variant="caption" color="text.disabled">·</Typography>
                <Chip
                  label="Arsenal"
                  size="small"
                  color="secondary"
                  title={t('hardcoded.components_common_sidebar.arsenal_admin')}
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}
                />
              </>
            )}
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.4}>
              © {new Date().getFullYear()} Arsenal Tire Performance Tech
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.4}>
              APS{APP_VERSION ? ` v${APP_VERSION}` : ''}
            </Typography>
          </Box>
        </Box>
      )}

      {collapsed && !isMobile && user && (
        <Box
          sx={{
            p: 1,
            borderTop: '1px solid',
            borderTopColor: 'divider',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Tooltip
            title={`${user?.name} ${user?.last_name} (${currentCompany?.role_display || t('audit.table.user')})`}
            placement="right"
            arrow
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: theme.palette.secondary.main,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={open}
          onClose={onClose}
          sx={{
            '& .MuiDrawer-paper': {
              width: currentWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderRightColor: 'divider',
              bgcolor: 'background.paper',
              top: navbarHeight,
              height: `calc(100% - ${navbarHeight}px)`,
            },
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="persistent"
          anchor="left"
          open={true}
          sx={{
            width: currentWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: currentWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderRightColor: 'divider',
              bgcolor: 'background.paper',
              top: navbarHeight,
              height: `calc(100% - ${navbarHeight}px)`,
              position: 'fixed',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {companies.length > 0 && (
        <Menu
          anchorEl={companyMenuAnchor}
          open={Boolean(companyMenuAnchor)}
          onClose={handleCompanyMenuClose}
          PaperProps={{
            sx: {
              maxHeight: 500,
              minWidth: 300,
              mt: 1
            }
          }}
        >
          {/* Search Box */}
          <Box sx={{ p: 1.5, pb: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('hardcoded.components_common_sidebar.buscar_empresa')}
              value={companySearchTerm}
              onChange={(e) => setCompanySearchTerm(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1
                }
              }}
            />
          </Box>

          {(companySearchTerm.trim() === '' || t('hardcoded.components_alarmprofiles_assignmentstable.todos').includes(companySearchTerm.trim().toLowerCase())) && (
            <MenuItem
              onClick={handleSwitchAllCompanies}
              disabled={switchingCompany}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: allCompaniesView ? 'primary.main' : 'grey.300',
                    fontSize: '0.875rem'
                  }}
                >
                  T
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</Typography>
                    {allCompaniesView && (
                      <CheckIcon color="primary" fontSize="small" />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {t('hardcoded.components_common_sidebar.ver_esquematicos_de_todas_las')}</Typography>
                }
              />
            </MenuItem>
          )}

          {/* Filtered Companies List */}
          {companies
            .filter((company) =>
              company.company_name.toLowerCase().includes(companySearchTerm.toLowerCase())
            )
            .map((company) => (
              <MenuItem
                key={company.company_id}
                onClick={() => handleSwitchCompany(company.company_id)}
                disabled={switchingCompany}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: !allCompaniesView && company.company_id === currentCompany?.company_id
                        ? 'primary.main'
                        : 'grey.300',
                      fontSize: '0.875rem'
                    }}
                  >
                    {company.company_name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {company.company_name}
                      </Typography>
                      {!allCompaniesView && company.company_id === currentCompany?.company_id && (
                        <CheckIcon color="primary" fontSize="small" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {company.role_display || company.role}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {i18n.t('audit.filters.from')}{new Date(company.joined_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                />
              </MenuItem>
            ))}

          {companies.filter((company) =>
            company.company_name.toLowerCase().includes(companySearchTerm.toLowerCase())
          ).length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'center' }}>
                  {t('hardcoded.components_common_sidebar.no_hay_empresas_coincidentes')}</Typography>
              </MenuItem>
            )}
        </Menu>
      )}

      {/* 🔄 WEBSOCKET: Animación para badge de alarmas */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </>
  );
};

export default Sidebar;